import {css, StyleSheet} from 'aphrodite';
import React, {useState, useEffect, useMemo} from 'react';

import {GetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import {
  filterInvalidColumnsFromColumnMapping,
  getDefaultDataFrameColumnMapping,
  isDataFrameColumnMappingComplete,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {AnalysisTemplateDataFrameVariableGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import AnalysisTemplateInputTableColumnMapper from 'analysis-tool/templates/table-mapping/AnalysisTemplateInputTableColumnMapper';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';

import AnalysisTemplateConfigFrame, {ConfigStatus} from './AnalysisTemplateConfigFrame';

type IsColumnMappingValidArgs = Pick<AnalysisTemplateInputTableConfigProps, 'variable' | 'columnMapping'> & {
  selectedInputTableInfo: Pick<InputTableInfo, 'columns'> | null;
};

/**
 * Check a column mapping is valid.
 *
 * A column mapping is valid if:
 * • Every column in `templateVariableInputTable.dataFrame.allColumnsJson` is mapped, and
 * • Every key in `columnMapping` is a column system name from `templateVariableInputTable.dataFrame`, and
 * • Every value in `columnMapping` is a column system name from `selectedInputTableInfo.columns`
 *
 * A valid mapping may include duplicate columns from `selectedInputTableInfo.columns`.
 */
export function isColumnMappingValid({
  variable,
  selectedInputTableInfo,
  columnMapping,
}: IsColumnMappingValidArgs): boolean {
  return (
    selectedInputTableInfo != null &&
    isDataFrameColumnMappingComplete(
      variable.replaceDataFrameColumnsSpec,
      filterInvalidColumnsFromColumnMapping(
        variable.replaceDataFrameColumnsSpec,
        selectedInputTableInfo.columns,
        columnMapping ?? {}
      )
    )
  );
}

/**
 * Get the draft column mapping to show the user when they first open edit mode.
 *
 * If `columnMapping` is `null`, a default mapping will be inferred.
 *
 * Otherwise, the existing mapping will be shown, with invalid columns removed.
 */
function getInitialDraftColumnMapping(
  templateDataFrame: Pick<GetAnalysisDataFrame, 'allColumnsJson'>,
  selectedInputTableInfo: InputTableInfo | null,
  columnMapping: Record<string, string> | null
): Record<string, string> {
  if (selectedInputTableInfo == null) {
    return {};
  } else if (columnMapping == null) {
    return getDefaultDataFrameColumnMapping(templateDataFrame.allColumnsJson, selectedInputTableInfo.columns);
  } else {
    return filterInvalidColumnsFromColumnMapping(
      templateDataFrame.allColumnsJson,
      selectedInputTableInfo.columns,
      columnMapping
    );
  }
}

interface InputTableInfo {
  /**
   * The user-facing name of the selected input table.
   */
  name: string;
  /**
   * The columns of the selected input table
   */
  columns: DataFrameColumns;
}

export interface AnalysisTemplateInputTableConfigProps {
  variable: AnalysisTemplateDataFrameVariableGraphQL;
  /**
   * Information definining the selected input table, or null if no input table has been selected.
   *
   * If no input table has been selected, the column mapping cannot be edited.
   */
  selectedInputTableInfo: InputTableInfo | null;
  /**
   * The user-selected column mapping from `templateVariableInputTable.allColumnsJson` to
   * `selectedInputTableInfo.columns`, or `null` if no mapping has been set.
   *
   * If `columnMapping` is `null`, an inferred column mapping will be defaulted, for the user to edit before
   * confirming. (`setColumnMapping` will not be called until the user clicks "Save".)
   *
   * Each key of `mappedColumns` is a column system name from `templateVariableInputTable.dataFrame`, and
   * values are column system names from `selectedInputTableInfo.columns`.
   *
   * The mapping may be incomplete, i.e. columns from `templateVariableInputTable.dataFrame` that have not
   * been mapped will be absent. To check the mapping is complete, see `isDataFrameColumnMappingComplete`.
   */
  columnMapping: Record<string, string> | null;
  /**
   * Callback to update the column mapping.
   *
   * The callback is called when the user clicks "Save", which is only enabled if the mapping is complete.
   *
   * The mapping passed to the callback will be complete, i.e. every column system name from
   * `templateVariableInputTable.dataFrame` will be included.
   */
  setColumnMapping: (newMapping: Record<string, string>) => void;
}

/**
 * Component to configure the column mapping for an Analysis Template input table, during Procedure configuration.
 *
 * This is a wrapper around `AnalysisTemplateInputTableColumnMapper`, that:
 * • Is styled for use in Procedure configuration.
 * • Displays compactly, until the user clicks "Edit" to expand the component.
 * • In "Edit" mode, has "Save" and "Cancel" buttons.
 * • Prevents saving incomplete column mappings.
 */
export default function AnalysisTemplateInputTableConfig(
  props: AnalysisTemplateInputTableConfigProps
): JSX.Element {
  const isMappingValid = isColumnMappingValid(props);
  const initialDraftColumnMapping = useMemo(
    () =>
      getInitialDraftColumnMapping(
        {allColumnsJson: props.variable.replaceDataFrameColumnsSpec},
        props.selectedInputTableInfo,
        props.columnMapping
      ),
    [props.variable, props.selectedInputTableInfo, props.columnMapping]
  );
  const [draftColumnMapping, setDraftColumnMapping] =
    useState<Record<string, string>>(initialDraftColumnMapping);
  const [inEditMode, setInEditMode] = useState<boolean>(false);

  useEffect(() => {
    if (props.selectedInputTableInfo == null) {
      setInEditMode(false);
    }
    setDraftColumnMapping(initialDraftColumnMapping);
  }, [props.selectedInputTableInfo, initialDraftColumnMapping]);

  if (props.selectedInputTableInfo == null) {
    return (
      <AnalysisTemplateConfigFrame headerTitle="Map Columns" message="Select a dataset to map columns" />
    );
  }

  const requiresColumnMapping = props.variable.replaceDataFrameColumnsSpec.length > 0;

  if (!inEditMode) {
    return (
      <AnalysisTemplateConfigFrame
        headerTitle="Map Columns"
        headerButtons={
          requiresColumnMapping && (
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              disabled={props.selectedInputTableInfo == null}
              onClick={() => {
                setInEditMode(true);
              }}
              size={ButtonSize.SMALL}
              text="Edit"
              dataTestAttrs={{
                component: 'AnalysisTemplateInputTableConfig',
                element: 'editButton',
              }}
            />
          )
        }
        status={isMappingValid ? ConfigStatus.COMPLETE : ConfigStatus.INVALID}
        message={
          !isMappingValid
            ? 'One or more columns not mapped'
            : !requiresColumnMapping
              ? 'Column mapping is not required'
              : null
        }
      />
    );
  }

  return (
    <AnalysisTemplateConfigFrame
      headerTitle="Map Columns"
      headerButtons={
        <>
          <Button
            buttonStyle={ButtonStyle.TEXT}
            onClick={() => {
              setInEditMode(false);
              setDraftColumnMapping(initialDraftColumnMapping);
            }}
            size={ButtonSize.SMALL}
            text="Cancel"
          />
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            className="u-mini-spacing-left"
            disabled={!isColumnMappingValid({...props, columnMapping: draftColumnMapping})}
            onClick={() => {
              setInEditMode(false);
              props.setColumnMapping(draftColumnMapping);
            }}
            size={ButtonSize.SMALL}
            text="Save"
            dataTestAttrs={{
              component: 'AnalysisTemplateInputTableConfig',
              element: 'saveButton',
            }}
          />
        </>
      }
    >
      <div className={css(styles.columnMapper)}>
        <AnalysisTemplateInputTableColumnMapper
          templateTableDataFrameColumns={props.variable.replaceDataFrameColumnsSpec}
          templateTableDescription={props.variable.description}
          templateTableLabel="Expected input"
          inputTableSelector={
            <Chip
              icon="table"
              isReadonly={true}
              text={props.selectedInputTableInfo.name}
              size={ChipSize.SMALL}
            />
          }
          inputTableSelectorLabel="Dataset to use"
          inputTableColumns={props.selectedInputTableInfo.columns}
          inputTableDataFrameIdForPreview={null}
          columnMapping={draftColumnMapping}
          setColumnMapping={setDraftColumnMapping}
        />
      </div>
    </AnalysisTemplateConfigFrame>
  );
}

const styles = StyleSheet.create({
  columnMapper: {
    borderRadius: 4,
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    overflow: 'hidden',
  },
});
